import styles from "./NomenclatureListPage.module.scss";
import React, { useCallback, useMemo, useState } from "react";
import {
  useForm,
  SubmitHandler,
  FieldValues,
  DefaultValues,
  FormProvider,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNomenclature } from "./hooks/useNomenclature";
import { ItemsList } from "../components/itemsList/ItemsList";
import { IconType } from "../icons/Icon";
import { INomenclatureEndpoints } from "./models/nomenclatureEndpoints";
import { useAddAdditionalLayoutButton } from "../infrastructure/hooks/useAddAdditionalLayoutButton";
import { AppDialog } from "../components/AppDialog/AppDialog";
import { NomenclatureListPageDialog } from "./components/NomenclatureListPageDialog";
import { useFormValidationMessages } from "../infrastructure/hooks/useFormHelper";
import { useAppContext } from "../../AppContext";
import { usePermissions } from "../infrastructure/hooks/usePermissions";
import { PageTitleSection } from "./components/PageTitleSection";
import { ReorderDialog, IReorderableItem } from "../components/ReorderDialog";
import { useApiMutation } from "../infrastructure/hooks/useApiMutation";
import { useQueryClient } from "@tanstack/react-query";
import { useItemsListFilters } from "../components/itemsList/hooks/useItemsListFilters";
import { IItemsListDeletedFilter } from "../components/itemsList/types/ItemsList.types";
import { ItemsListFiltersPanelContent } from "../components/itemsList/components/ItemsListFiltersPanelContent";
import { FilterIconButton } from "../components/filters/FilterIconButton";
import { FiltersSidePanel } from "../components/filters/FiltersSidePanel";
import { RenderWhen } from "../infrastructure/components/RenderWhen";

interface NomenclatureListPageProps<
  TFormModel extends FieldValues,
  TItemModel extends Partial<TFormModel> & { id?: number; name?: string },
> {
  endpoints: INomenclatureEndpoints;
  title: string;
  description?: string;
  dialogAddTitle: string;
  dialogEditTitle: string;
  emptyListText: string;
  itemTemplate: (
    item: TItemModel | undefined,
    index: number,
    isLoading: boolean,
    actionButtons?: React.ReactNode,
    className?: string,
  ) => JSX.Element;
  formSchema: yup.ObjectSchema<any>;
  defaultValues: () => DefaultValues<TFormModel>;
  editOnRowClick?: boolean;
  formFields: React.ReactNode;
  showReorderButton?: boolean;
  /**
   * Enables the filter icon next to the page title + the filters side panel.
   * Every nomenclature marks deletion the same way (isDeleted), so only the
   * storage key and the wording are supplied per page.
   */
  deletedFilter?: Omit<IItemsListDeletedFilter<TItemModel>, "isDeleted">;
}

export const NomenclatureListPage = <
  TFormModel extends FieldValues,
  TItemModel extends Partial<TFormModel> & { id?: number; name?: string },
>({
  endpoints,
  title,
  description,
  dialogAddTitle,
  dialogEditTitle,
  emptyListText,
  itemTemplate,
  formSchema,
  defaultValues,
  formFields,
  editOnRowClick = false,
  showReorderButton = false,
  deletedFilter,
}: NomenclatureListPageProps<TFormModel, TItemModel>) => {
  const { selectedStudioId } = useAppContext();
  const { userHavePermissions } = usePermissions();
  const queryClient = useQueryClient();
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

  const keysToInvalidate = useMemo(
    () => [
      [endpoints.getAll, selectedStudioId],
      [endpoints.getAllWithDeleted, selectedStudioId],
      [endpoints.getById, selectedStudioId],
    ],
    [
      endpoints.getAll,
      endpoints.getAllWithDeleted,
      endpoints.getById,
      selectedStudioId,
    ],
  );
  const {
    items,
    isLoading,
    dialogOpen,
    editingItemId,
    isSubmitting,
    setEditingItemId,
    setDialogOpen,
    create,
    update,
  } = useNomenclature<TItemModel>(endpoints, keysToInvalidate);

  // Memoised so the fallback array keeps its identity while the query is still
  // loading - both the filter below and ItemsList key their effects off it.
  const safeItems = useMemo(() => items ?? [], [items]);

  // Only pages with the filter get a count. Without it the list still renders
  // deleted rows, so an active-only count would contradict what is on screen
  // and there would be no filter panel to explain the difference.
  const activeItemsCount = useMemo(
    () => safeItems.filter((item) => !item.isDeleted).length,
    [safeItems],
  );

  const {
    isFiltersPanelOpen,
    openFiltersPanel,
    closeFiltersPanel,
    showDeleted,
    updateShowDeleted,
    clearAllFilters,
    visibleItems,
    activeFiltersCount,
  } = useItemsListFilters(
    safeItems,
    deletedFilter
      ? { ...deletedFilter, isDeleted: (item) => !!item.isDeleted }
      : undefined,
  );

  const handleReorderSuccess = useCallback(() => {
    keysToInvalidate.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: key }),
    );
    setReorderDialogOpen(false);
  }, [keysToInvalidate, queryClient]);

  const reorderMutation = useApiMutation({
    onSuccess: handleReorderSuccess,
  });
  const reorderMutate = reorderMutation.mutate;
  const isReordering = reorderMutation.isLoading;

  const form = useForm<TFormModel>({
    resolver: yupResolver(formSchema),
    defaultValues: defaultValues(),
    mode: "onSubmit",
  });
  useFormValidationMessages<TFormModel>(form);

  const dialogTitle = useMemo(
    () => (editingItemId ? dialogEditTitle : dialogAddTitle),
    [editingItemId, dialogAddTitle, dialogEditTitle],
  );

  const onEditClick = useCallback(
    (itemId?: number) => {
      setEditingItemId(itemId ?? null);
      form.reset(defaultValues());
      setDialogOpen(true);
    },
    [setEditingItemId, form, defaultValues, setDialogOpen],
  );

  const handleAddClick = useCallback(() => {
    onEditClick();
  }, [onEditClick]);

  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    buttonOnClick: handleAddClick,
    rawPath: endpoints.create,
  });

  const onHide = () => {
    form.reset(defaultValues());
    setDialogOpen(false);
  };

  const onSubmit: SubmitHandler<TFormModel> = async (data) => {
    const payload = data as unknown as Partial<TItemModel>;
    if (!!data.id) {
      update(data.id, payload);
    } else {
      create(payload);
    }
  };

  const handleRowClick = (item: TItemModel | undefined) => {
    const canEdit = userHavePermissions(endpoints.update);
    if (editOnRowClick && item?.id && !item.isDeleted && canEdit) {
      onEditClick(item.id);
    }
  };

  const itemsForReorder: IReorderableItem[] = useMemo(() => {
    return (items ?? [])
      .filter((item) => !item.isDeleted)
      .map((item) => ({
        id: item.id!,
        name: item.name ?? "",
      }));
  }, [items]);

  const handleReorder = useCallback(
    async (orderedIds: number[]) => {
      if (!endpoints.reorder) return;

      reorderMutate({
        endpoint: endpoints.reorder,
        method: "put",
        payload: { orderedIds },
      });
    },
    [endpoints.reorder, reorderMutate],
  );

  const showReorderBtn =
    showReorderButton &&
    endpoints.reorder &&
    userHavePermissions(endpoints.reorder) &&
    itemsForReorder.length > 1;

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <PageTitleSection
          title={title}
          description={description}
          isLoading={isLoading}
          showReorderButton={showReorderBtn}
          onReorderClick={() => setReorderDialogOpen(true)}
          itemsCount={
            !deletedFilter || isLoading ? undefined : activeItemsCount
          }
          filterButton={
            deletedFilter ? (
              <FilterIconButton
                onClick={openFiltersPanel}
                activeFiltersCount={activeFiltersCount}
              />
            ) : undefined
          }
        />

        <div className={styles.scrollable_content}>
          <ItemsList
            isLoading={isLoading}
            className={styles.root_list}
            items={visibleItems}
            noItemsText={emptyListText}
            itemTemplate={(item, index, actionButtons, className) => (
              <div
                onClick={() => handleRowClick(item)}
                style={{ width: "100%" }}
              >
                {itemTemplate(item, index, isLoading, actionButtons, className)}
              </div>
            )}
            swipeLeftActions={[
              {
                enablePrompt: true,
                promptMessage:
                  "Сигурни ли сте, че искате да изтриете този запис?",
                type: "other",
                method: "put",
                iconType: IconType.DeleteIcon,
                endpoint: endpoints.toggleDeletionStatus,
                validator: (item) => !item.isDeleted,
                invalidateKeys: keysToInvalidate,
              },
              {
                enablePrompt: true,
                promptMessage: "Да възстановим ли записа?",
                type: "other",
                method: "put",
                iconType: IconType.PutBackIcon,
                endpoint: `${endpoints.toggleDeletionStatus}`,
                validator: (item) => item.isDeleted ?? false,
                invalidateKeys: keysToInvalidate,
              },
            ]}
            swipeRightActions={[
              {
                type: "onClick",
                iconType: IconType.EditIcon,
                permissionRoute: endpoints.update,
                onClick: (item) => onEditClick(item.id),
                validator: (item) => !item.isDeleted,
              },
            ]}
            enableSearch
            fullHeight
          />
        </div>
      </div>

      <AppDialog
        visible={dialogOpen}
        header={dialogTitle}
        onHide={onHide}
        children={
          <FormProvider {...form}>
            <NomenclatureListPageDialog
              formFields={formFields}
              onSubmit={onSubmit}
              items={items}
              itemIdToUpdate={editingItemId}
              isLoading={isSubmitting}
            />
          </FormProvider>
        }
      />

      <RenderWhen condition={!!deletedFilter}>
        <FiltersSidePanel
          visible={isFiltersPanelOpen}
          onHide={closeFiltersPanel}
        >
          <ItemsListFiltersPanelContent
            showDeleted={showDeleted}
            sectionTitle={deletedFilter?.sectionTitle}
            toggleLabel={deletedFilter?.toggleLabel}
            onShowDeletedChange={updateShowDeleted}
            onClearAllFilters={clearAllFilters}
            onClose={closeFiltersPanel}
          />
        </FiltersSidePanel>
      </RenderWhen>

      <ReorderDialog
        visible={reorderDialogOpen}
        items={itemsForReorder}
        onHide={() => setReorderDialogOpen(false)}
        onSave={handleReorder}
        loading={isReordering}
        title="Подреди"
      />
    </div>
  );
};

export default NomenclatureListPage;
